import {
  Divider,
  Sheet,
  SheetBody,
  SheetContent,
  SheetHeader,
  SheetTitle,
  Typography,
} from "@suraasa/placebo-ui"
import { PronunciationFeedbackType } from "api/resources/learningItems/types"
import clsx from "clsx"
import Markdown from "markdown-to-jsx"
import React, { useState } from "react"

const FeedbackAnalysisSheet = ({
  open,
  handleClose,
  pronunciationFeedback,
}: {
  open: boolean
  handleClose: () => void
  pronunciationFeedback: PronunciationFeedbackType
}) => {
  const [scrolled, setScrolled] = useState(false)

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.currentTarget
    setScrolled(target.scrollTop > 0) // true if scrolled down
  }
  return (
    <Sheet open={open} onOpenChange={() => handleClose()}>
      <SheetContent height={90} className="max-w-3xl rounded-t-2xl !p-0">
        <SheetHeader
          className={clsx(
            "border-b  border-transparent p-4 pb-2.5 transition-all",
            {
              " shadow-[0px_6px_15px_0px_#0000000D] border-onSurface-300":
                scrolled,
            }
          )}
        >
          <SheetTitle>
            <Typography variant="title3">Pronunciation Breakdown</Typography>
          </SheetTitle>
        </SheetHeader>
        <SheetBody onScroll={handleScroll} className="px-4 pb-8">
          <div className="flex flex-col gap-2">
            {pronunciationFeedback.detailedAnalysis.map((item, index) => (
              <div className="flex flex-col gap-2" key={index}>
                <div className="flex items-center justify-between rounded-xl border border-surface-300 bg-surface-100 p-1.5 text-onSurface-900">
                  <Typography variant="title4">
                    {item.word} ({item.ipaTranscription})
                  </Typography>
                  <Typography variant="smallBody">
                    <Typography variant="title4" className="inline">
                      {item.score}
                    </Typography>{" "}
                    / 100
                  </Typography>
                </div>
                {item.feedback && (
                  <Typography className="text-onSurface-500">
                    <Markdown>{item.feedback}</Markdown>
                  </Typography>
                )}
                {item.howToImprove.length > 0 && (
                  <div>
                    <Typography variant="subtitle2" className="mb-0.5 py-0.75">
                      How to Improve:
                    </Typography>
                    <ol className="list-outside list-decimal pl-2 text-onSurface-500">
                      {item.howToImprove.map(i => (
                        <li key={i}>
                          <Typography>
                            <Markdown>{i}</Markdown>
                          </Typography>
                        </li>
                      ))}
                    </ol>
                  </div>
                )}
              </div>
            ))}
            <Divider />
            <div>
              <Typography variant="subtitle2" className="mb-0.5 py-0.75">
                Practice Drills:
              </Typography>
              <div className="text-onSurface-500">
                <Typography>
                  Focus on keeping your lips relaxed for the first word and
                  slightly rounded for the second.
                </Typography>
                <ul className="list-outside list-disc pl-5 text-onSurface-500">
                  {pronunciationFeedback.practiceDrills.minimalPairs.map(i => (
                    <li key={i}>
                      <Typography>
                        <Markdown>{i}</Markdown>
                      </Typography>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            <div>
              <Typography variant="subtitle2" className="mb-0.5">
                Try This Sentence
              </Typography>
              <Typography className="pl-0.25 text-onSurface-500">
                &quot;
                <Markdown>
                  {pronunciationFeedback.practiceDrills.practiceSentence}
                </Markdown>
                &quot;
              </Typography>
            </div>
          </div>
        </SheetBody>
      </SheetContent>
    </Sheet>
  )
}

export default FeedbackAnalysisSheet
